import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, CheckCircle2, Loader, ExternalLink, Eye } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { track } from '../../lib/analytics'
import { PLANS, SUBSCRIPTION_FEATURES, formatUSD, formatCOP } from '../../constants/pricing'
import TrialBadge from './TrialBadge'

/**
 * PaywallBanner — shown in CajaDashboardPage when subscription_status = 'trial'
 * (soft) or 'expired' (hard modal). Lets the user pick monthly or annual and
 * redirects to MercadoPago Checkout Pro for that plan.
 */
export default function PaywallBanner({ isTrial = false }) {
  const navigate = useNavigate()
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  // Anual por default — mejor conversión + ahorra 33%
  const [selected, setSelected] = useState('annual')

  useEffect(() => {
    track('caja_paywall_viewed', { mode: isTrial ? 'trial_soft' : 'hard_modal' })
  }, [isTrial])

  async function handlePay() {
    setLoading(true)
    setError('')
    track('caja_paywall_pay_clicked', { plan_id: selected, mode: isTrial ? 'trial_soft' : 'hard_modal' })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-mp-preference`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ planId: selected }),
        }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Error al crear el pago')

      const checkoutUrl = import.meta.env.DEV
        ? data.sandbox_init_point
        : data.init_point
      window.location.href = checkoutUrl
    } catch (err) {
      setError(err.message ?? 'Ocurrió un error. Intenta de nuevo.')
      setLoading(false)
    }
  }

  // ── Soft trial banner ───────────────────────────────────────────────────────
  if (isTrial) {
    const plan = PLANS[selected]
    return (
      <div className="rounded-2xl border border-accent-200 bg-gradient-to-br from-accent-50 via-yellow-50/60 to-cream-100 p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <Sparkles size={16} className="text-accent-500" />
              <span className="text-sm font-bold text-gray-800">Estás en modo de prueba</span>
              <TrialBadge variant="inline" />
            </div>
            <p className="text-xs text-gray-500 leading-relaxed max-w-md">
              Activa tu suscripción para guardar tu plan, descargarlo en Word/PDF
              y mantener acceso continuo a las guías y al agente de IA.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <PlanToggle selected={selected} onChange={setSelected} compact />
            <button
              onClick={handlePay}
              disabled={loading}
              className="flex items-center gap-2 bg-brand-800 hover:bg-brand-900 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap shadow-sm"
            >
              {loading
                ? <><Loader size={14} className="animate-spin" /> Redirigiendo…</>
                : <><Sparkles size={14} /> Activar · {formatUSD(plan.priceUSD)}</>
              }
            </button>
            <span className="text-[11px] text-gray-500 tabular-nums">
              ≈ {formatCOP(plan.priceCOP)} · pago seguro con MercadoPago
            </span>
          </div>
        </div>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>
    )
  }

  // ── Hard paywall modal ──────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center mx-auto mb-4">
          <Sparkles size={26} className="text-brand-700" />
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Activa tu plan estratégico
        </h2>
        <p className="text-gray-500 text-sm mb-6 leading-relaxed">
          Tu período de prueba ha terminado. Elige el plan que mejor te sirva — puedes cambiar después.
        </p>

        <ul className="text-left space-y-2 mb-6">
          {SUBSCRIPTION_FEATURES.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
              <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              {f}
            </li>
          ))}
        </ul>

        <PlanToggle selected={selected} onChange={setSelected} />

        {error && (
          <p className="text-sm text-red-600 mt-4 mb-2 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
        )}

        <button
          onClick={handlePay}
          disabled={loading}
          className="w-full mt-5 flex items-center justify-center gap-2 bg-brand-800 hover:bg-brand-900 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-colors text-base shadow-sm"
        >
          {loading
            ? <><Loader size={16} className="animate-spin" /> Redirigiendo a MercadoPago…</>
            : <><ExternalLink size={16} /> Pagar {formatUSD(PLANS[selected].priceUSD)} con MercadoPago</>
          }
        </button>
        <p className="text-[11px] text-gray-500 mt-2 text-center tabular-nums">
          ≈ {formatCOP(PLANS[selected].priceCOP)} · {PLANS[selected].durationMonths === 1 ? '1 mes' : '12 meses'} de acceso completo
        </p>

        <button
          onClick={() => navigate('/caja/demo')}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-900 hover:underline transition-colors"
        >
          <Eye size={12} /> Antes de pagar, mira un plan ejemplo navegable
        </button>

        <p className="text-xs text-gray-400 mt-4 leading-relaxed">
          Cobramos en COP a la TRM del día. Aceptamos tarjetas, PSE y efectivo.
          <br />¿Tienes dudas? <a href="mailto:estrategia@nextvalleyco.com" className="text-brand-600 hover:underline">estrategia@nextvalleyco.com</a>
        </p>
      </div>
    </div>
  )
}

// ─── Inline plan toggle ────────────────────────────────────────────────────────
function PlanToggle({ selected, onChange, compact = false }) {
  const ids = ['monthly', 'annual']

  if (compact) {
    return (
      <div className="inline-flex rounded-xl bg-white border border-gray-200 p-0.5 shadow-sm">
        {ids.map(id => {
          const plan = PLANS[id]
          const active = selected === id
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange(id)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                active ? 'bg-brand-700 text-white' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {plan.durationMonths === 1 ? 'Mensual' : 'Anual'}
              {plan.savings && !active && <span className="ml-1 text-emerald-500">-{plan.savings}</span>}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-3 mt-2">
      {ids.map(id => {
        const plan = PLANS[id]
        const active = selected === id
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            className={`relative text-left rounded-xl border-2 p-4 transition-all ${
              active
                ? 'border-brand-700 bg-brand-50 shadow-sm'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            {plan.badge && (
              <span className="absolute -top-2 right-3 text-[10px] font-bold uppercase tracking-wide bg-brand-700 text-white px-2 py-0.5 rounded-full">
                {plan.badge}
              </span>
            )}
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              {plan.durationMonths === 1 ? 'Mensual' : 'Anual'}
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {formatUSD(plan.priceUSD)}
              <span className="text-xs text-gray-400 font-normal">
                /{plan.durationMonths === 1 ? 'mes' : 'año'}
              </span>
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">≈ {formatCOP(plan.priceCOP)}</p>
            {plan.savings && (
              <p className="text-[11px] text-emerald-600 font-semibold mt-1">Ahorras {plan.savings} vs. mensual</p>
            )}
          </button>
        )
      })}
    </div>
  )
}
